import React, { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';

import { Typography, Skeleton, Badge} from 'antd'
import {  Card,  Row, Col, Placeholder, Form } from 'react-bootstrap';

import Flex         from 'components/common/Flex';
import Notification from 'components/invoices/analytics/partials/Notification';
import Background   from 'components/common/Background';

import { getColor } from 'helpers/utils';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  TitleComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  CanvasRenderer
]);

import InvoiceService from 'components/invoices/services';
import {capitalize, formatCurrency} from 'helpers/utils'
import { useFormatPrice } from 'helpers/settings/settingUtils';
import { useAuthSettingUtils } from 'helpers/settings/settingUtils';

const TodayAnalytics = () => {
  const { getAuthCurrency } = useAuthSettingUtils();
  const currency = getAuthCurrency();

  const [isLoading, setIsLoading] = useState(true);
  const [totalPaid, setTotalPaid] = useState(0.00);
  const [totalCreatedBill, setTotalCreatedBill] = useState(0.00);
  const [daily_data, setDailyData] = useState([]);
  const [currency_symbol, setCurrencySymbol] = useState(currency.symbol);
  const [currency_code, setCurrencyCode] = useState(currency.code);
  const [dateTimeZone, setDateTimeZone] = useState('');
  const [currentDateTime, setCurrentDateTime] = useState('');
  const [calRange, setCalRange] = useState(1);
  const [calRangeName, setCalRangeName] = useState('Past 7 days');
  const [percentage_invoices, setPercentageInvoices] = useState(0);
  const [percentage_payments, setPercentagePayments] = useState(0);

  const getData = useCallback(() => {
    InvoiceService.getTodayAnalytics().then( (response) => {
      try {
        if( response.data !==  undefined && response.data.data !==  undefined ) {        
          let result = response.data.data;
          
          setDailyData(result.daily_data.map(day => [day.date, day.total_invoices, day.total_payments]));
          setTotalPaid(result.trend_analysis.current_period.total_payments);
          setTotalCreatedBill(result.trend_analysis.current_period.total_invoices);
          setCurrencySymbol(result.currency.currency_symbol);
          setCurrencyCode(result.currency.currency_code);
          setPercentageInvoices(result.trend_analysis.percentage_changes.invoices);
          setPercentagePayments(result.trend_analysis.percentage_changes.payments);
          setIsLoading(false);
        }      

        var now = new Date(new Date().toLocaleString(undefined, {timeZone: dateTimeZone}));
        var seconds = now.getSeconds();
        var minutes = now.getMinutes();
        var hours = now.getHours() % 12;

      } catch(error){
        console.log(error);
      }
    });
  }, [dateTimeZone]);

  useEffect(() => {
    getData();
  }, [getData]);

  const getInvoicesOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      formatter: function(params) {
        return `${params[0].name}<br/>
                Invoices: ${formatCurrency(params[0].value[1], currency_symbol)}`;
      },
      transitionDuration: 0,
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('dark') },
      borderWidth: 1
    },
    xAxis: {
      type: 'category',
      data: daily_data.map(item => item[0]),
      boundaryGap: false,
      axisLine: { show: false },
      axisLabel: { show: false },
      axisTick: { show: false },
      axisPointer: { type: 'none' }
    },
    yAxis: {
      type: 'value',
      splitLine: { show: false },
      axisLine: { show: false },
      axisLabel: { show: false },
      axisTick: { show: false },
      axisPointer: { type: 'none' }
    },
    series: [
      {
        type: 'bar',
        showBackground: true,
        backgroundStyle: {
          borderRadius: 10
        },
        barWidth: '5px',
        itemStyle: {
          borderRadius: 10,
          color: getColor('primary')
        },
        data: daily_data.map(item => [item[0], item[1]]),
        z: 10
      }
    ],
    grid: { right: 5, left: 10, top: 0, bottom: 0 }
  }), [daily_data]);

  const getPaymentsOption = useMemo(() => ({
    tooltip: {
      trigger: 'axis',
      padding: [7, 10],
      formatter: function(params) {
        return `${params[0].name}<br/>
                Payments: ${formatCurrency(params[0].value[1], currency_symbol)}`;
      },
      transitionDuration: 0,
      backgroundColor: getColor('gray-100'),
      borderColor: getColor('gray-300'),
      textStyle: { color: getColor('dark') },
      borderWidth: 1
    },
    xAxis: {
      type: 'category',
      data: daily_data.map(item => item[0]),
      boundaryGap: false,
      axisLine: { show: false },
      axisLabel: { show: false },
      axisTick: { show: false },
      axisPointer: { type: 'none' }
    },
    yAxis: {
      type: 'value',
      splitLine: { show: false },
      axisLine: { show: false },
      axisLabel: { show: false },
      axisTick: { show: false },
      axisPointer: { type: 'none' }
    },
    series: [
      {
        type: 'bar',
        showBackground: true,
        backgroundStyle: {
          borderRadius: 10
        },
        barWidth: '5px',
        itemStyle: {
          borderRadius: 10,
          color: getColor('success')
        },
        data: daily_data.map(item => [item[0], item[2]]),
        z: 10
      }
    ],
    grid: { right: 5, left: 10, top: 0, bottom: 0 }
  }), [daily_data]);

  const { Text } = Typography;

  return (
    <Card className="mb-1 p-0"> 
    {(isLoading ? 
      <Fragment>
        <div className="p-3">
          <Skeleton active />
        </div>
      </Fragment>
      : 
      <Fragment>
        <Row className='mt-0 mb-0 gx-0 '>
          <Flex className="">
            <div className="pt-3 pb-3 flex-fill border-end">

              <Flex>
                <div className="ms-2 flex-fill">
                  <Text className="mb-0 fs--1">{capitalize(calRangeName)}'s Total Invoices</Text>
                  <h4 className="mb-0">{formatCurrency(totalCreatedBill, currency_symbol)} <span className='fs-0 text-400'>
                    {currency_code}</span>
                  </h4>
                  <Badge 
                    
                    style={{ backgroundColor: percentage_invoices >= 0 ? '#52c41a' : '#52c41a' }}
                    count={`${percentage_invoices}%`}
                    className="fs--1"
                  />
                </div>   

                <div className="m-2">
                <ReactEChartsCore
                  echarts={echarts}
                  option={getInvoicesOption}
                  opts={{ renderer: 'canvas' }}
                  style={{ width: '5.5rem', height: 40 }}
                />
                </div>
              </Flex>
            </div>
            <div className="pt-3 pb-3 flex-fill">
            <Flex>
              <div className="ms-2 flex-fill">
                <Text className="mb-0 fs--1">{capitalize(calRangeName)} received payments</Text>

                <h4 className="mb-0">{formatCurrency(totalPaid, currency_symbol)} <span className='fs-0 text-400'>{currency_code}</span></h4>
                <Badge 
                    style={{ backgroundColor: percentage_payments >= 0 ? '#52c41a' : '#ff4d4f' }}
                    count={`${percentage_payments}%`}
                    className="fs--1"
                  />                  
              </div>
              <div className="m-2">
                
              <ReactEChartsCore
                  echarts={echarts}
                  option={getPaymentsOption}
                  opts={{ renderer: 'canvas' }}
                  style={{ width: '5.5rem', height: 40 }}
                />            
              </div>
            </Flex>
            </div>
          </Flex>
        </Row>
      </Fragment>
    )}
    </Card>
  )
};

export default TodayAnalytics;
